import React, { useState, useRef, useEffect } from 'react';
import { DatabaseStats } from '../types';

interface APIResponse {
  status: number;
  data: any;
  headers: any;
  error?: string;
}

interface APIEndpoint {
  method: string;
  path: string;
  description: string;
  parameters?: {
    name: string;
    type: string;
    required: boolean;
    description: string;
    example?: string;
  }[];
  responseExample: any;
  category: string;
}

// SVG Icons
const CodeIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
  </svg>
);

const PlayIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z"/>
  </svg>
);

const CheckIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CopyIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const DownloadIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg style={style} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V9a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const API_ENDPOINTS: APIEndpoint[] = [
  {
    method: 'GET',
    path: '/stats',
    description: 'Get database statistics and overview metrics',
    category: 'Core API',
    responseExample: {
      "total_entries": 531,
      "validated_entries": 489,
      "unique_proteins": 231,
      "unique_journals": 127
    }
  },
  {
    method: 'GET',
    path: '/search',
    description: 'Search protein exosite entries with filtering options',
    category: 'Core API',
    parameters: [
      {
        name: 'protein_name',
        type: 'string',
        required: false,
        description: 'Protein name to search for',
        example: 'thrombin'
      },
      {
        name: 'pdb_id',
        type: 'string',
        required: false,
        description: 'PDB structure ID (4 characters)',
        example: '1PPB'
      },
      {
        name: 'organism',
        type: 'string',
        required: false,
        description: 'Source organism',
        example: 'human'
      },
      {
        name: 'per_page',
        type: 'number',
        required: false,
        description: 'Results per page (max: 100)',
        example: '20'
      }
    ],
    responseExample: {
      "entries": [
        {
          "entry_type": "grouped",
          "grouped_entry": {
            "protein_name": "THROMBIN",
            "entry_count": 163,
            "representative_pdb_ids": ["1PPB", "1HAG"],
            "evidence_score": 1.0
          }
        }
      ],
      "total_results": 1
    }
  },
  {
    method: 'GET',
    path: '/entries/{id}',
    description: 'Get detailed entry information by ID',
    category: 'Core API',
    parameters: [
      {
        name: 'id',
        type: 'integer',
        required: true,
        description: 'Entry identifier',
        example: '123'
      }
    ],
    responseExample: {
      "id": 123,
      "protein_name": "THROMBIN",
      "pdb_id": "1PPB",
      "exosite_residues_reported": "Arg35, Arg67, Arg101",
      "organism": "human",
      "evidence_score": 0.95
    }
  }
];

interface APIComponentProps {}

const APIComponent: React.FC<APIComponentProps> = () => {
  const [selectedEndpoint, setSelectedEndpoint] = useState<APIEndpoint>(API_ENDPOINTS[0]);
  const [activeCategory, setActiveCategory] = useState<string>('Core API');
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [response, setResponse] = useState<APIResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const responseRef = useRef<HTMLDivElement>(null);

  const categories = Array.from(new Set(API_ENDPOINTS.map(endpoint => endpoint.category)));

  const buildUrl = (endpoint: APIEndpoint): string => {
    const baseUrl = '';
    
    let path = endpoint.path;
    
    // Replace path parameters
    if (endpoint.parameters) {
      endpoint.parameters.forEach(param => {
        if (path.includes(`{${param.name}}`)) {
          path = path.replace(`{${param.name}}`, parameters[param.name] || param.example || '');
        }
      });
    }

    // Add query parameters
    const queryParams = new URLSearchParams();
    if (endpoint.parameters) {
      endpoint.parameters.forEach(param => {
        if (!path.includes(`{${param.name}}`)) { // Not a path parameter
          const value = parameters[param.name];
          if (value) {
            queryParams.append(param.name, value);
          }
        }
      });
    }

    const queryString = queryParams.toString();
    return `${baseUrl}${path}${queryString ? '?' + queryString : ''}`;
  };

  const executeRequest = async () => {
    setLoading(true);
    setResponse(null);

    try {
      const url = buildUrl(selectedEndpoint);
      const startTime = Date.now();
      
      const fetchResponse = await fetch(url, {
        method: selectedEndpoint.method,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const responseTime = Date.now() - startTime;
      const responseData = await fetchResponse.json();

      setResponse({
        status: fetchResponse.status,
        data: responseData,
        headers: {
          'content-type': fetchResponse.headers.get('content-type'),
          'response-time': `${responseTime}ms`
        }
      });

    } catch (error: any) {
      setResponse({
        status: 0,
        data: null,
        headers: {},
        error: error.message || 'Network error'
      });
    } finally {
      setLoading(false);
    }
  };

  const generateCodeExample = (language: 'javascript' | 'python' | 'curl'): string => {
    const url = buildUrl(selectedEndpoint);
    
    if (language === 'javascript') {
      return `// JavaScript (fetch)\nconst response = await fetch('${url}');\nconst data = await response.json();`;
    } else if (language === 'python') {
      return `# Python (requests)\nimport requests\n\nresponse = requests.get('${url}')\ndata = response.json()\nprint(data)`;
    } else if (language === 'curl') {
      return `# cURL\ncurl -X ${selectedEndpoint.method} \\\\\n  -H "Content-Type: application/json" \\\\\n  "${url}"`;
    } else {
      return '';
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  useEffect(() => {
    if (response && responseRef.current) {
      responseRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [response]);

  const filteredEndpoints = API_ENDPOINTS.filter(endpoint => endpoint.category === activeCategory);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
          <CodeIcon style={{ height: '32px', width: '32px', color: '#22c55e' }} />
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>
            ExositeDB API
          </h1>
        </div>
        <p style={{ fontSize: '20px', color: '#64748b', maxWidth: '768px', margin: '0 auto 24px' }}>
          RESTful API for programmatic access to curated protein exosite data with comprehensive documentation and interactive testing.
        </p>
        
        {/* API Status */}
        <div style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          gap: '8px',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          <CheckIcon style={{ height: '16px', width: '16px' }} />
          API Status: Online
        </div>
      </div>

      <div style={{ display: 'flex', gap: '32px' }}>
        {/* Left Sidebar - Endpoint List */}
        <div style={{ width: '320px' }}>
          <div className="card" style={{ position: 'sticky', top: '20px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
              API Endpoints
            </h3>
            
            {/* Category Tabs */}
            <div style={{ marginBottom: '16px' }}>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 12px',
                    margin: '4px 0',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: activeCategory === category ? '#f0fdf4' : 'transparent',
                    color: activeCategory === category ? '#16a34a' : '#64748b',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (activeCategory !== category) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeCategory !== category) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Endpoint List */}
            <div>
              {filteredEndpoints.map((endpoint, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedEndpoint(endpoint)}
                  style={{
                    padding: '12px',
                    border: selectedEndpoint === endpoint ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    backgroundColor: selectedEndpoint === endpoint ? '#eff6ff' : 'white',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedEndpoint !== endpoint) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedEndpoint !== endpoint) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{
                      backgroundColor: endpoint.method === 'GET' ? '#10b981' : '#ef4444',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '600',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontFamily: 'monospace'
                    }}>
                      {endpoint.method}
                    </span>
                    <code style={{ 
                      fontSize: '14px', 
                      color: '#1e293b',
                      fontWeight: '500'
                    }}>
                      {endpoint.path}
                    </code>
                  </div>
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#64748b', 
                    margin: 0,
                    lineHeight: '1.4'
                  }}>
                    {endpoint.description.split('.')[0]}.
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '24px' }}>
            {/* Left Column - Endpoint Details */}
            <div>
              {/* Endpoint Details */}
              <div className="card" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <span style={{
                    backgroundColor: selectedEndpoint.method === 'GET' ? '#10b981' : '#ef4444',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontFamily: 'monospace'
                  }}>
                    {selectedEndpoint.method}
                  </span>
                  <code style={{ 
                    fontSize: '20px', 
                    color: '#1e293b',
                    fontWeight: '600'
                  }}>
                    {selectedEndpoint.path}
                  </code>
                </div>
                
                <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '24px' }}>
                  {selectedEndpoint.description}
                </p>

                {/* Parameters */}
                {selectedEndpoint.parameters && selectedEndpoint.parameters.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
                      Parameters
                    </h4>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {selectedEndpoint.parameters.map((param, index) => (
                        <div key={index} style={{ 
                          border: '1px solid #e2e8f0', 
                          borderRadius: '6px', 
                          padding: '12px' 
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <code style={{ 
                              fontSize: '14px', 
                              fontWeight: '600',
                              color: '#1e293b'
                            }}>
                              {param.name}
                            </code>
                            <span style={{
                              backgroundColor: '#f1f5f9',
                              color: '#475569',
                              fontSize: '11px',
                              padding: '2px 6px',
                              borderRadius: '3px'
                            }}>
                              {param.type}
                            </span>
                            {param.required && (
                              <span style={{
                                backgroundColor: '#ef4444',
                                color: 'white',
                                fontSize: '10px',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontWeight: '500'
                              }}>
                                required
                              </span>
                            )}
                          </div>
                          
                          <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 8px 0' }}>
                            {param.description}
                          </p>
                          
                          <input
                            type="text"
                            placeholder={param.example || `Enter ${param.name}...`}
                            value={parameters[param.name] || ''}
                            onChange={(e) => setParameters(prev => ({ ...prev, [param.name]: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '6px 10px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '13px',
                              fontFamily: 'monospace'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Try It Out */}
                <div>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '12px' }}>
                    Try It Out
                  </h4>
                  
                  <div style={{ 
                    backgroundColor: '#f8fafc', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '6px', 
                    padding: '10px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Request URL:</div>
                    <code style={{ 
                      fontSize: '12px', 
                      color: '#1e293b',
                      wordBreak: 'break-all'
                    }}>
                      {buildUrl(selectedEndpoint)}
                    </code>
                  </div>
                  
                  <button
                    onClick={executeRequest}
                    disabled={loading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: loading ? '#9ca3af' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <PlayIcon style={{ height: '14px', width: '14px' }} />
                    {loading ? 'Executing...' : 'Execute'}
                  </button>
                </div>
              </div>

              {/* Code Examples */}
              <div className="card">
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                  Code Examples
                </h4>
                
                {['javascript', 'python', 'curl'].map((language) => (
                  <div key={language} style={{ marginBottom: '12px' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: '6px'
                    }}>
                      <h5 style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        color: '#374151',
                        margin: 0,
                        textTransform: 'capitalize'
                      }}>
                        {language}
                      </h5>
                      <button
                        onClick={() => copyToClipboard(generateCodeExample(language as any))}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '3px 6px',
                          backgroundColor: copiedCode ? '#10b981' : '#f1f5f9',
                          color: copiedCode ? 'white' : '#64748b',
                          border: '1px solid #e2e8f0',
                          borderRadius: '3px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {copiedCode ? <CheckIcon style={{ height: '10px', width: '10px' }} /> : <CopyIcon style={{ height: '10px', width: '10px' }} />}
                        {copiedCode ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    
                    <div style={{ 
                      backgroundColor: '#1e293b', 
                      border: '1px solid #374151', 
                      borderRadius: '4px', 
                      padding: '10px',
                      overflow: 'auto'
                    }}>
                      <pre style={{ 
                        color: '#e2e8f0', 
                        fontSize: '11px', 
                        margin: 0,
                        fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
                      }}>
                        {generateCodeExample(language as any)}
                      </pre>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Response */}
            <div style={{ position: 'sticky', top: '20px', height: 'fit-content' }}>
              {/* Response Section */}
              {response ? (
                <div className="card">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                      Response
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        backgroundColor: response.status >= 200 && response.status < 300 ? '#10b981' : '#ef4444',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '3px 8px',
                        borderRadius: '4px'
                      }}>
                        {response.error ? 'Error' : response.status}
                      </span>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                        {response.headers['response-time']}
                      </span>
                    </div>
                  </div>
                  
                  <div style={{ 
                    backgroundColor: '#1e293b', 
                    border: '1px solid #374151', 
                    borderRadius: '6px', 
                    padding: '12px',
                    overflow: 'auto',
                    maxHeight: '400px'
                  }}>
                    <pre style={{ 
                      color: '#e2e8f0', 
                      fontSize: '12px', 
                      margin: 0,
                      fontFamily: 'Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      lineHeight: '1.4'
                    }}>
                      {response.error 
                        ? `Error: ${response.error}`
                        : JSON.stringify(response.data, null, 2)
                      }
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <CodeIcon style={{ 
                    height: '48px', 
                    width: '48px', 
                    color: '#cbd5e1', 
                    margin: '0 auto 16px' 
                  }} />
                  <h4 style={{ 
                    fontSize: '16px', 
                    fontWeight: '500', 
                    color: '#64748b', 
                    margin: 0 
                  }}>
                    Execute a request to see the response
                  </h4>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default APIComponent;